/**
 * Session Manager utility for handling authentication tokens
 * Supports both localStorage and HTTP-only cookies
 */

export class SessionManager {
  private static readonly TOKEN_KEY = 'auth_token';
  private static readonly USER_KEY = 'auth_user';

  /**
   * Store authentication token in localStorage
   */
  static setToken(token: string): void {
    try {
      localStorage.setItem(this.TOKEN_KEY, token);
    } catch (error) {
      console.warn('Failed to store token in localStorage:', error);
    }
  }

  /**
   * Get authentication token from localStorage
   */
  static getToken(): string | null {
    try {
      return localStorage.getItem(this.TOKEN_KEY);
    } catch (error) {
      console.warn('Failed to retrieve token from localStorage:', error);
      return null;
    }
  }

  /**
   * Remove authentication token from localStorage
   */
  static removeToken(): void {
    try {
      localStorage.removeItem(this.TOKEN_KEY);
    } catch (error) {
      console.warn('Failed to remove token from localStorage:', error);
    }
  }

  /**
   * Store user data in localStorage
   */
  static setUser(user: any): void {
    try {
      localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    } catch (error) {
      console.warn('Failed to store user in localStorage:', error);
    }
  }

  /**
   * Get user data from localStorage
   */
  static getUser(): any | null {
    try {
      const userData = localStorage.getItem(this.USER_KEY);
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.warn('Failed to retrieve user from localStorage:', error);
      return null;
    }
  }

  /**
   * Remove user data from localStorage
   */
  static removeUser(): void {
    try {
      localStorage.removeItem(this.USER_KEY);
    } catch (error) {
      console.warn('Failed to remove user from localStorage:', error);
    }
  }

  /**
   * Clear all session data
   */
  static clearSession(): void {
    this.removeToken();
    this.removeUser();
  }

  /**
   * Check if token is expired
   */
  static isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp < currentTime;
    } catch (error) {
      console.warn('Failed to parse token for expiry check:', error);
      return true; // Assume expired if we can't parse
    }
  }

  /**
   * Get time until token expires (in milliseconds)
   */
  static getTimeUntilExpiry(token: string): number {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiryTime = payload.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      return Math.max(0, expiryTime - currentTime);
    } catch (error) {
      console.warn('Failed to calculate time until expiry:', error);
      return 0;
    }
  }

  /**
   * Check if token needs refresh (within 5 minutes of expiry)
   */
  static needsRefresh(token: string): boolean {
    const timeUntilExpiry = this.getTimeUntilExpiry(token);
    const fiveMinutes = 5 * 60 * 1000; // 5 minutes in milliseconds
    return timeUntilExpiry <= fiveMinutes;
  }

  /**
   * Validate session integrity
   */
  static validateSession(): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }

    if (this.isTokenExpired(token)) {
      this.clearSession();
      return false;
    }

    return true;
  }
}
